(() => {
  const STORAGE_PREFIX = 'flowerness-farm:';

  function isShopifyDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function setupHeader(root = document) {
    const headers = root.matches?.('[data-ff-header]')
      ? [root]
      : root.querySelectorAll?.('[data-ff-header]') || [];

    headers.forEach((header) => {
      setupHeaderDetails(header);
      setupAnnouncement(header);
    });
  }

  function setupHeaderDetails(header) {
    const detailsElements = header.querySelectorAll('details');

    detailsElements.forEach((details) => {
      if (details.dataset.ffInitialized === 'true') return;

      details.dataset.ffInitialized = 'true';

      details.addEventListener('toggle', () => {
        if (!details.open) return;

        header.querySelectorAll('details[open]').forEach((other) => {
          if (other !== details && !other.contains(details)) {
            other.removeAttribute('open');
          }
        });
      });
    });

    /*
     * Close the mobile drawer after a customer chooses a link.
     */
    const mobileMenu = header.querySelector('[data-ff-mobile-menu]');

    if (mobileMenu && mobileMenu.dataset.ffLinkInitialized !== 'true') {
      mobileMenu.dataset.ffLinkInitialized = 'true';

      mobileMenu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          mobileMenu.removeAttribute('open');
        });
      });
    }
  }

  function setupAnnouncement(header) {
    const announcement = header.querySelector('[data-ff-announcement]');

    if (!announcement) return;

    if (announcement.dataset.ffInitialized === 'true') return;

    announcement.dataset.ffInitialized = 'true';

    const closeButton = announcement.querySelector(
      '[data-ff-announcement-close]'
    );

    const shouldPersist =
      announcement.dataset.persistDismissal === 'true';

    const rawStorageKey =
      announcement.dataset.storageKey ||
      'default-announcement';

    const storageKey =
      STORAGE_PREFIX + rawStorageKey;

    /*
     * Do not hide announcements automatically inside the Shopify
     * Theme Editor. Otherwise the merchant could close it once
     * and wonder where the section went.
     */
    if (!isShopifyDesignMode() && shouldPersist) {
      try {
        if (localStorage.getItem(storageKey) === 'dismissed') {
          announcement.hidden = true;
        }
      } catch (error) {
        // Storage can be unavailable in strict privacy environments.
      }
    }

    if (!closeButton) return;

    closeButton.addEventListener('click', () => {
      announcement.hidden = true;

      if (!isShopifyDesignMode() && shouldPersist) {
        try {
          localStorage.setItem(storageKey, 'dismissed');
        } catch (error) {
          // Failing to persist should never break the header.
        }
      }
    });
  }

  function init() {
    setupHeader(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /*
   * Shopify reloads sections dynamically while editing.
   * Reinitialize the newly rendered header.
   */
  document.addEventListener('shopify:section:load', (event) => {
    setupHeader(event.target);
  });

  /*
   * When a section is selected in the editor, ensure its
   * announcement is visible even if it was previously closed
   * during that preview session.
   */
  document.addEventListener('shopify:section:select', (event) => {
    if (!isShopifyDesignMode()) return;

    const announcement =
      event.target.querySelector?.('[data-ff-announcement]');

    if (announcement) {
      announcement.hidden = false;
    }
  });
})();


/* ============================================================
   FLOWERNESS FARM - SCROLL POSITION RESTORATION
   Horizon uses .page-wrapper as its scrolling container.
   This preserves the visitor's position when refreshing a page.
   ============================================================ */

(() => {
  const SCROLL_KEY_PREFIX = 'flowerness-scroll:';

  function isThemeEditor() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function getScrollContainer() {
    const pageWrapper = document.querySelector('.page-wrapper');

    if (pageWrapper) {
      return pageWrapper;
    }

    return document.scrollingElement || document.documentElement;
  }

  function getPageKey() {
    return (
      SCROLL_KEY_PREFIX +
      window.location.pathname +
      window.location.search
    );
  }

  function getNavigationType() {
    const navigationEntry =
      performance.getEntriesByType &&
      performance.getEntriesByType('navigation')[0];

    if (navigationEntry) {
      return navigationEntry.type;
    }

    /*
     * Fallback for older browsers.
     */
    if (
      performance.navigation &&
      performance.navigation.type === 1
    ) {
      return 'reload';
    }

    return 'navigate';
  }

  function saveScrollPosition() {
    if (isThemeEditor()) return;

    const container = getScrollContainer();

    if (!container) return;

    const position =
      container === document.scrollingElement ||
      container === document.documentElement
        ? window.scrollY || container.scrollTop || 0
        : container.scrollTop;

    try {
      sessionStorage.setItem(
        getPageKey(),
        String(position)
      );
    } catch (error) {
      /*
       * Storage may be disabled in private browsing.
       * Never let that break the storefront.
       */
    }
  }

  function restoreScrollPosition() {
    if (isThemeEditor()) return;

    /*
     * If the URL intentionally contains #something,
     * allow the browser to navigate to that anchor instead.
     */
    if (window.location.hash) return;

    /*
     * Only restore after a genuine page refresh.
     * Normal navigation to another page should begin at the top.
     */
    if (getNavigationType() !== 'reload') return;

    let savedPosition;

    try {
      savedPosition = sessionStorage.getItem(
        getPageKey()
      );
    } catch (error) {
      return;
    }

    if (savedPosition === null) return;

    const targetPosition = Number(savedPosition);

    if (
      Number.isNaN(targetPosition) ||
      targetPosition < 0
    ) {
      return;
    }

    const container = getScrollContainer();

    if (!container) return;

    const restore = () => {
      if (
        container === document.scrollingElement ||
        container === document.documentElement
      ) {
        window.scrollTo({
          top: targetPosition,
          left: 0,
          behavior: 'auto'
        });
      } else {
        container.scrollTo({
          top: targetPosition,
          left: 0,
          behavior: 'auto'
        });
      }
    };

    /*
     * Restore several times because images, Shopify sections,
     * product recommendations and app blocks can alter page
     * height after DOMContentLoaded.
     */
    requestAnimationFrame(() => {
      restore();

      requestAnimationFrame(() => {
        restore();
      });
    });

    window.addEventListener(
      'load',
      () => {
        restore();

        setTimeout(restore, 100);
        setTimeout(restore, 350);
        setTimeout(restore, 800);
      },
      { once: true }
    );
  }

  /*
   * Save continuously enough that even an immediate refresh
   * remembers the latest position.
   */
  let scrollTimer;

  const container = getScrollContainer();

  const scrollTarget =
    container === document.scrollingElement ||
    container === document.documentElement
      ? window
      : container;

  if (scrollTarget) {
    scrollTarget.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollTimer);

        scrollTimer = setTimeout(
          saveScrollPosition,
          80
        );
      },
      { passive: true }
    );
  }

  /*
   * Save again just before the document disappears.
   */
  window.addEventListener(
    'pagehide',
    saveScrollPosition
  );

  window.addEventListener(
    'beforeunload',
    saveScrollPosition
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') {
        saveScrollPosition();
      }
    }
  );

  /*
   * Disable native document restoration because Horizon
   * does not primarily scroll the normal document.
   */
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  restoreScrollPosition();
})();



/* ============================================================
   FLOWERNESS FARM - DESKTOP MEGA MENU
   Hover + keyboard + click support
   ============================================================ */

(() => {

  function closeMegaMenus(except = null) {

    document
      .querySelectorAll(
        '[data-ff-nav-item].is-open'
      )
      .forEach((item) => {

        if (item === except) return;

        item.classList.remove('is-open');

        const button =
          item.querySelector(
            '[data-ff-menu-toggle]'
          );

        if (button) {
          button.setAttribute(
            'aria-expanded',
            'false'
          );
        }

      });

  }


  function initializeMegaMenus(root = document) {

    const toggles =
      root.querySelectorAll?.(
        '[data-ff-menu-toggle]'
      ) || [];


    toggles.forEach((button) => {

      if (
        button.dataset.ffMegaInitialized ===
        'true'
      ) {
        return;
      }


      button.dataset.ffMegaInitialized =
        'true';


      button.addEventListener(
        'click',
        (event) => {

          event.preventDefault();

          event.stopPropagation();


          const item =
            button.closest(
              '[data-ff-nav-item]'
            );


          if (!item) return;


          const willOpen =
            !item.classList.contains(
              'is-open'
            );


          closeMegaMenus(item);


          item.classList.toggle(
            'is-open',
            willOpen
          );


          button.setAttribute(
            'aria-expanded',
            willOpen
              ? 'true'
              : 'false'
          );

        }
      );


      button.addEventListener(
        'keydown',
        (event) => {

          if (
            event.key !== 'ArrowDown'
          ) {
            return;
          }


          event.preventDefault();


          const item =
            button.closest(
              '[data-ff-nav-item]'
            );


          if (!item) return;


          closeMegaMenus(item);


          item.classList.add(
            'is-open'
          );


          button.setAttribute(
            'aria-expanded',
            'true'
          );


          const firstLink =
            item.querySelector(
              '.ff-mega-menu a'
            );


          if (firstLink) {
            firstLink.focus();
          }

        }
      );

    });

  }


  document.addEventListener(
    'click',
    (event) => {

      if (
        !event.target.closest(
          '[data-ff-nav-item]'
        )
      ) {
        closeMegaMenus();
      }

    }
  );


  document.addEventListener(
    'keydown',
    (event) => {

      if (event.key === 'Escape') {

        closeMegaMenus();

      }

    }
  );


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => initializeMegaMenus()
    );

  } else {

    initializeMegaMenus();

  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initializeMegaMenus(
        event.target
      );

    }
  );

})();

/* ============================================================
   FLOWERNESS FARM - PREMIUM MEGA MENU HOVER INTENT
   ============================================================ */

(() => {
  const CLOSE_DELAY = 280;

  function initializeFlowernessHoverMenus(root = document) {
    const items =
      root.querySelectorAll?.(
        '[data-ff-nav-item].ff-nav-item--has-children'
      ) || [];

    items.forEach((item) => {
      if (item.dataset.ffHoverInitialized === 'true') {
        return;
      }

      item.dataset.ffHoverInitialized = 'true';

      let closeTimer = null;

      const openMenu = () => {
        if (window.matchMedia('(max-width: 990px)').matches) {
          return;
        }

        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }

        /*
         * Close hover-open state on siblings.
         * A deliberately clicked menu using .is-open is left alone.
         */
        document
          .querySelectorAll(
            '[data-ff-nav-item].is-hover-open'
          )
          .forEach((other) => {
            if (other !== item) {
              other.classList.remove('is-hover-open');
            }
          });

        item.classList.add('is-hover-open');

        const toggle =
          item.querySelector('[data-ff-menu-toggle]');

        if (toggle) {
          toggle.setAttribute(
            'aria-expanded',
            'true'
          );
        }
      };

      const scheduleClose = () => {
        if (window.matchMedia('(max-width: 990px)').matches) {
          return;
        }

        if (closeTimer) {
          clearTimeout(closeTimer);
        }

        closeTimer = setTimeout(() => {
          item.classList.remove('is-hover-open');

          const toggle =
            item.querySelector('[data-ff-menu-toggle]');

          /*
           * If it was explicitly clicked open, retain its
           * aria-expanded state.
           */
          if (
            toggle &&
            !item.classList.contains('is-open')
          ) {
            toggle.setAttribute(
              'aria-expanded',
              'false'
            );
          }
        }, CLOSE_DELAY);
      };

      item.addEventListener(
        'mouseenter',
        openMenu
      );

      item.addEventListener(
        'mouseleave',
        scheduleClose
      );

      /*
       * Keep the menu open while keyboard focus remains inside.
       */
      item.addEventListener(
        'focusin',
        openMenu
      );

      item.addEventListener(
        'focusout',
        () => {
          requestAnimationFrame(() => {
            if (!item.contains(document.activeElement)) {
              scheduleClose();
            }
          });
        }
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeFlowernessHoverMenus()
    );
  } else {
    initializeFlowernessHoverMenus();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeFlowernessHoverMenus(event.target);
    }
  );
})();